import { formatUserFacingError } from '@/lib/errors'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle2, ExternalLink, Kanban, Loader2, Mail, Plus, X } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { testimonialService } from '@/features/testimonials'
import { CreateBoardDialog } from '@/features/projectBoards/components/CreateBoardDialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AdminListPage } from '../components/AdminListPage'
import { ClientEngagementModal } from '../components/ClientEngagementModal'
import { useAdminClientEngagements } from '../hooks/useAdminClientEngagements'
import { getEstimateEmailDisplay, timestampToMillis } from '../lib/estimateEmailStatus'
import type { ClientEngagementDocument, EngagementStatus } from '@/types'
import type { EngagementFormValues } from '../types'

const STATUS_BADGE: Record<
  EngagementStatus,
  { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }
> = {
  scheduled:           { label: 'Scheduled',           variant: 'secondary'   },
  awaiting_acceptance: { label: 'Awaiting Acceptance', variant: 'outline'     },
  awaiting_payment:    { label: 'Awaiting Deposit',    variant: 'outline'     },
  confirmed:           { label: 'Confirmed',           variant: 'default'     },
  in_progress:         { label: 'In Progress',         variant: 'default'     },
  delivered:           { label: 'Delivered',           variant: 'outline'     },
  cancelled:           { label: 'Cancelled',           variant: 'destructive' },
}

const STATUS_FILTER_OPTIONS: { value: EngagementStatus | ''; label: string }[] = [
  { value: '',                    label: 'All Statuses'       },
  { value: 'scheduled',           label: 'Scheduled'          },
  { value: 'awaiting_acceptance', label: 'Awaiting Acceptance'},
  { value: 'awaiting_payment',    label: 'Awaiting Deposit'   },
  { value: 'confirmed',           label: 'Confirmed'          },
  { value: 'in_progress',         label: 'In Progress'        },
  { value: 'delivered',           label: 'Delivered'          },
  { value: 'cancelled',           label: 'Cancelled'          },
]

function formatDate(iso: string | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatTimestampDate(value: unknown): string {
  const milliseconds = timestampToMillis(value)
  return milliseconds === null ? '—' : new Date(milliseconds).toLocaleDateString()
}

function formatCurrency(amount: number | undefined): string {
  if (amount === undefined) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

function billingSummary(engagement: ClientEngagementDocument): string {
  if (engagement.finalPaidAt) return 'Paid in full'
  if (engagement.stripeFinalInvoiceId) return 'Final invoice due'
  if (engagement.finalInvoiceReviewReadyAt) return 'Final invoice ready'
  if (engagement.depositPaidAt) return 'Deposit paid'
  if (engagement.stripeDepositInvoiceId) return 'Deposit due'
  if (engagement.acceptedAt) return 'Estimate accepted'
  if (engagement.estimateSentAt) return 'Estimate sent'
  if (engagement.estimatePreparedAt) return 'Estimate prepared'
  return '—'
}

function EstimateEmailStatus({ engagement }: { engagement: ClientEngagementDocument }) {
  const display = getEstimateEmailDisplay(engagement)
  const sentAtMs = timestampToMillis(engagement.estimateSentAt)
  const sentDate = sentAtMs === null
    ? null
    : new Date(sentAtMs).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })

  if (display.state === 'sent') {
    return (
      <div className="space-y-1">
        <Badge variant="outline" className="gap-1 border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="h-3 w-3" />
          Sent
        </Badge>
        {sentDate && <div className="text-[10px] text-muted-foreground">{sentDate}</div>}
      </div>
    )
  }

  if (display.state === 'failed' || display.state === 'stalled') {
    return (
      <Badge variant="destructive" className="gap-1" title={display.description}>
        <AlertCircle className="h-3 w-3" />
        {display.state === 'stalled' ? 'Needs attention' : 'Failed'}
      </Badge>
    )
  }

  if (display.state === 'sending') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        Sending
      </Badge>
    )
  }

  return <span className="text-xs text-muted-foreground">Not sent</span>
}

export function AdminClientEngagementsPage() {
  const {
    engagements,
    members,
    equipment,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    createEngagement,
    updateEngagement,
    deleteEngagement,
    lookupUserByEmail,
    createClientUser,
    refresh,
  } = useAdminClientEngagements()

  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const clientFilter = searchParams.get('clientUserId') ?? ''

  const visibleEngagements = useMemo(
    () => (clientFilter ? engagements.filter((e) => e.clientUserId === clientFilter) : engagements),
    [engagements, clientFilter],
  )
  const estimateEmailAttentionCount = useMemo(
    () => visibleEngagements.filter((engagement) => {
      const state = getEstimateEmailDisplay(engagement).state
      return state === 'failed' || state === 'stalled'
    }).length,
    [visibleEngagements],
  )
  const clientFilterLabel = useMemo(() => {
    if (!clientFilter) return null
    const match = engagements.find((e) => e.clientUserId === clientFilter)
    return match ? (match.clientName || match.clientEmail || clientFilter) : clientFilter
  }, [engagements, clientFilter])

  function clearClientFilter() {
    const next = new URLSearchParams(searchParams)
    next.delete('clientUserId')
    setSearchParams(next, { replace: true })
  }
  const [modalOpen, setModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<(ClientEngagementDocument & { id: string }) | null>(null)
  const [sendingSurveyId, setSendingSurveyId] = useState<string | null>(null)
  const [createBoardTarget, setCreateBoardTarget] = useState<(ClientEngagementDocument & { id: string }) | null>(null)

  function handleOpenBoard(e: React.MouseEvent, eng: ClientEngagementDocument & { id: string }) {
    e.stopPropagation()
    if (eng.boardId) {
      navigate(`/admin/client-services/engagements/${eng.id}/board`)
      return
    }
    setCreateBoardTarget(eng)
  }

  async function handleSendSurveyEmail(e: React.MouseEvent, engagementId: string) {
    e.stopPropagation()
    if (!confirm('Send survey email to this client?')) return
    setSendingSurveyId(engagementId)
    try {
      await testimonialService.requestSurveyEmail(engagementId, user!.uid)
    } catch (err) {
      alert(formatUserFacingError(err, 'Failed to send survey email.'))
    } finally {
      setSendingSurveyId(null)
    }
  }

  function openCreate() {
    setEditTarget(null)
    setModalOpen(true)
  }

  function openEdit(eng: ClientEngagementDocument & { id: string }) {
    setEditTarget(eng)
    setModalOpen(true)
  }

  async function handleSave(data: EngagementFormValues): Promise<void> {
    if (editTarget) {
      await updateEngagement(editTarget.id, data)
    } else {
      await createEngagement(data)
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!confirm('Delete this engagement? This cannot be undone.')) return
    await deleteEngagement(id)
  }

  return (
    <>
      <AdminListPage
        title="Client Engagements"
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New Engagement
          </Button>
        }
        filters={
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={statusFilter || '__all'}
              onValueChange={(v) => setStatusFilter(v === '__all' ? '' : v as EngagementStatus)}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || '__all'} value={opt.value || '__all'}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {clientFilter && (
              <Badge variant="secondary" className="gap-1 pl-2 pr-1 py-1">
                <span className="text-xs">
                  Client: <span className="font-medium">{clientFilterLabel}</span>
                </span>
                <button
                  type="button"
                  onClick={clearClientFilter}
                  aria-label="Clear client filter"
                  className="ml-1 rounded-sm hover:bg-background/60 p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
          </div>
        }
        loading={loading}
        error={error}
        skeletonRows={6}
        dialog={
          <ClientEngagementModal
            open={modalOpen}
            onOpenChange={setModalOpen}
            engagement={editTarget ?? undefined}
            members={members}
            equipment={equipment}
            onSave={handleSave}
            onLookupUser={lookupUserByEmail}
            onCreateClient={createClientUser}
            onRefresh={refresh}
          />
        }
      >
        {estimateEmailAttentionCount > 0 && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {estimateEmailAttentionCount} estimate email {estimateEmailAttentionCount === 1 ? 'needs' : 'emails need'} attention
            </AlertTitle>
            <AlertDescription>
              Open the engagement marked Failed or Needs attention to review the error and retry.
            </AlertDescription>
          </Alert>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Estimate</TableHead>
              <TableHead>Estimate email</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead className="text-center">Files</TableHead>
              <TableHead className="text-center">Survey</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleEngagements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="py-8 text-center text-muted-foreground">
                  {clientFilter ? 'No engagements for this client.' : 'No engagements yet.'}
                </TableCell>
              </TableRow>
            ) : (
              visibleEngagements.map((eng) => (
                <TableRow
                  key={eng.id}
                  className="cursor-pointer"
                  onClick={() => openEdit(eng)}
                >
                  <TableCell>
                    <div className="font-medium text-sm">{eng.clientName}</div>
                    <div className="text-xs text-muted-foreground">{eng.clientEmail}</div>
                  </TableCell>
                  <TableCell className="max-w-[180px]">
                    <span className="truncate block text-sm">{eng.title}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_BADGE[eng.status].variant}>
                      {STATUS_BADGE[eng.status].label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {eng.assignedMemberName ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(eng.scheduledDate)}
                  </TableCell>
                  <TableCell className="text-sm font-mono">
                    {formatCurrency(eng.estimateAmount)}
                  </TableCell>
                  <TableCell>
                    <EstimateEmailStatus engagement={eng} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {billingSummary(eng)}
                  </TableCell>
                  <TableCell className="text-center">
                    {eng.driveFolderUrl || eng.driveLink ? (
                      <a
                        href={eng.driveFolderUrl ?? eng.driveLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Open Drive link"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {eng.status === 'delivered' && (
                      <div className="flex flex-col items-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          disabled={sendingSurveyId === eng.id}
                          onClick={(e) => void handleSendSurveyEmail(e, eng.id)}
                        >
                          <Mail className="mr-1 h-3 w-3" />
                          {sendingSurveyId === eng.id
                            ? 'Sending…'
                            : eng.surveyEmailSentAt
                              ? 'Resend'
                              : 'Send Survey'}
                        </Button>
                        {eng.surveyEmailSentAt && (
                          <span className="text-[10px] text-muted-foreground">
                            Sent {formatTimestampDate(eng.surveyEmailSentAt)}
                          </span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={(e) => handleOpenBoard(e, eng)}
                        title={eng.boardId ? 'Open project board' : 'Create project board'}
                      >
                        <Kanban className="mr-1 h-3 w-3" />
                        {eng.boardId ? 'Open board' : 'Create board'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) => {
                          e.stopPropagation()
                          void handleDelete(eng.id)
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </AdminListPage>
      {createBoardTarget && (
        <CreateBoardDialog
          open
          engagement={createBoardTarget}
          onClose={() => {
            setCreateBoardTarget(null)
            void refresh()
          }}
        />
      )}
    </>
  )
}
